import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { DocumentVerrou, DocumentVerrouType, decrireVerrous } from "src/app/core/models/document-verrou";
import { AuthenticationService } from "src/app/core/services/auth.service";
import { DocumentVerrouService } from "src/app/core/services/document-verrou.service";
import { ToastService } from "src/app/core/services/toast.service";

const INTERVALLE_MS = 30 * 1000;

/**
 * Bandeau « ce document est en cours de réception / modification par … », comme le message de Sage.
 * Il interroge l'état des verrous toutes les 30 secondes tant que `actif` est vrai, émet les verrous des
 * AUTRES utilisateurs (le parent désactive ses actions), et propose à l'administrateur de forcer le déverrouillage.
 *
 * Il garde aussi la main de l'administrateur : après un forçage, ou quand le parent ouvre une correction
 * (`garderLaMain`), le verrou de modification est renouvelé tant que l'administrateur est actif sur la page
 * (sinon il expirerait au bout de 10 minutes et le magasinier pourrait de nouveau enregistrer), et rendu dès qu'il
 * quitte la page (`lacherLaMain` / navigation / onglet fermé).
 */
@Component({
	selector: "app-document-verrou-bandeau",
	templateUrl: "./document-verrou-bandeau.component.html",
})
export class DocumentVerrouBandeauComponent implements OnChanges, OnDestroy {
	@Input() documentType: DocumentVerrouType;
	@Input() documentId: number;
	/** « Ce bon de commande » */
	@Input() libelle = "Ce document";
	/** Faux : pas d'interrogation (l'utilisateur ne peut de toute façon rien modifier ici). */
	@Input() actif = true;
	/** Verrous tenus par d'autres utilisateurs ; vide quand le document est libre. */
	@Output() verrousChange = new EventEmitter<DocumentVerrou[]>();

	protected verrous: DocumentVerrou[] = [];
	protected isForcing = false;
	private minuterie: any = null;
	/** Document dont l'administrateur a la main (forçage ou correction en cours) : renouvelé, puis rendu en quittant la page. */
	private documentTenu: number | null = null;
	private minuterieMain: any = null;
	/** Sans activité depuis le dernier passage, le verrou n'est pas renouvelé : il expire tout seul (page laissée ouverte). */
	private activite = false;

	constructor(
		private verrouService: DocumentVerrouService,
		private authService: AuthenticationService,
		private toast: ToastService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		this.arreter();
		if (this.documentTenu !== null && this.documentTenu !== this.documentId) this.libererMain();
		if (!this.actif || !this.documentType || !this.documentId) {
			this.publier([]);
			return;
		}
		this.rafraichir();
		this.minuterie = setInterval(() => this.rafraichir(), INTERVALLE_MS);
	}

	ngOnDestroy(): void {
		this.arreter();
		this.libererMain();
	}

	@HostListener("document:keydown")
	@HostListener("document:click")
	protected marquerActivite() {
		this.activite = true;
	}

	// Onglet fermé ou rechargé : la libération part quand même (sinon le verrou expire au bout de 10 minutes)
	@HostListener("window:pagehide")
	protected auDechargement() {
		if (this.documentTenu !== null) this.verrouService.libererAuDechargement(this.documentType, [this.documentTenu], "modification");
	}

	/** Le parent vient de prendre le verrou de modification (ouverture d'une correction) : on le renouvelle tant que l'administrateur est là. */
	public garderLaMain(): void {
		if (!this.documentId) return;
		this.documentTenu = this.documentId;
		this.activite = true;
		this.arreterRenouvellement();
		this.minuterieMain = setInterval(() => this.renouvelerMain(), DocumentVerrouService.INTERVALLE_RENOUVELLEMENT_MS);
	}

	/** Le parent a rendu lui-même le verrou (fermeture de la correction) : plus rien à renouveler ni à rendre. */
	public lacherLaMain(): void {
		this.documentTenu = null;
		this.arreterRenouvellement();
	}

	private renouvelerMain() {
		if (this.documentTenu === null || !this.activite) return;
		this.activite = false;
		// Refusé seulement si le verrou a expiré entre-temps et qu'un magasinier l'a repris : le bandeau le dira au prochain passage
		this.verrouService.acquerir(this.documentType, [this.documentTenu], "modification").subscribe({ error: () => {} });
	}

	/** L'administrateur quitte le document : il rend la main, le magasinier peut de nouveau enregistrer. */
	private libererMain() {
		if (this.documentTenu === null) return;
		const documentId = this.documentTenu;
		this.lacherLaMain();
		this.verrouService.liberer(this.documentType, [documentId], "modification").subscribe({ error: () => {} });
	}

	private arreterRenouvellement() {
		if (this.minuterieMain) clearInterval(this.minuterieMain);
		this.minuterieMain = null;
	}

	private arreter() {
		if (this.minuterie) clearInterval(this.minuterie);
		this.minuterie = null;
	}

	get description(): string {
		return decrireVerrous(this.verrous);
	}
	get isAdmin(): boolean {
		return this.authService.isAdmin();
	}

	/** À appeler par le parent quand une action vient d'être refusée pour cause de verrou. */
	public rafraichir(): void {
		if (!this.actif || !this.documentId) return;
		this.verrouService.etat(this.documentType, [this.documentId]).subscribe({
			next: (response) => this.publier((response.data || []).filter((verrou) => !verrou.estMoi)),
			// Verrous indisponibles (permission non accordée) : pas de bandeau, le backend contrôle à l'enregistrement
			error: () => this.publier([]),
		});
	}

	private publier(verrous: DocumentVerrou[]) {
		const avant = this.verrous.map((verrou) => verrou.id).join(",");
		this.verrous = verrous;
		if (avant !== verrous.map((verrou) => verrou.id).join(",")) this.verrousChange.emit(verrous);
	}

	protected forcer() {
		if (!confirm(`Forcer le déverrouillage ? ${this.libelle} est ${this.description}. Vous prenez la main : son enregistrement sera refusé tant que vous restez sur cette page (ou jusqu'à la fin de votre correction).`)) return;
		this.isForcing = true;
		this.verrouService.forcer(this.documentType, this.documentId).subscribe({
			next: () => {
				this.garderLaMain();
				this.toast.success("Document déverrouillé");
				this.isForcing = false;
				this.publier([]);
			},
			error: (error) => {
				this.toast.error(error?.error?.error?.message || "Le déverrouillage a échoué");
				this.isForcing = false;
			},
		});
	}
}
